import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react'
import { swapSelectedCollageCells } from '@/tools/collage/actions'

const DRAG_START_DISTANCE = 6
const TOUCH_LONG_PRESS_MS = 350

interface ActivePointer {
  id: number
  cellIndex: number
  pointerType: string
  startX: number
  startY: number
  cancelledTap: boolean
  filled: boolean
}

function eventCell(element: HTMLButtonElement): { index: number; filled: boolean } {
  const index = Number(element.dataset.collageCellIndex)
  if (!Number.isInteger(index) || index < 0) throw new Error('Collage cell pointer target has an invalid index')
  const filledValue = element.dataset.collageCellFilled
  if (filledValue !== 'true' && filledValue !== 'false') {
    throw new Error('Collage cell pointer target is missing its filled state')
  }
  return { index, filled: filledValue === 'true' }
}

export function useCollageCellSwap(activateCell: (cellIndex: number) => void) {
  const active = useRef<ActivePointer | null>(null)
  const dragging = useRef(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropTarget = useRef<number | null>(null)
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  const clearLongPress = () => {
    if (longPressTimer.current !== null) clearTimeout(longPressTimer.current)
    longPressTimer.current = null
  }

  const reset = () => {
    clearLongPress()
    active.current = null
    dragging.current = false
    dropTarget.current = null
    setDragSourceIndex(null)
    setDropTargetIndex(null)
  }

  const startDrag = (cellIndex: number) => {
    dragging.current = true
    setDragSourceIndex(cellIndex)
  }

  const handlers = {
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return
      event.stopPropagation()
      const cell = eventCell(event.currentTarget)
      event.currentTarget.setPointerCapture(event.pointerId)
      active.current = {
        id: event.pointerId,
        cellIndex: cell.index,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        cancelledTap: false,
        filled: cell.filled,
      }
      if (event.pointerType === 'touch' && cell.filled) {
        longPressTimer.current = setTimeout(() => {
          if (active.current?.id === event.pointerId) startDrag(cell.index)
          longPressTimer.current = null
        }, TOUCH_LONG_PRESS_MS)
      }
    },
    onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
      const current = active.current
      if (!current || current.id !== event.pointerId) return
      const distance = Math.hypot(event.clientX - current.startX, event.clientY - current.startY)
      if (!dragging.current && distance >= DRAG_START_DISTANCE) {
        if (current.pointerType === 'touch' || !current.filled) {
          clearLongPress()
          current.cancelledTap = true
          return
        }
        startDrag(current.cellIndex)
      }
      if (!dragging.current) return
      event.preventDefault()
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-collage-cell-index]')
      const value = target?.dataset.collageCellIndex
      const targetIndex = value === undefined ? null : Number(value)
      const nextTarget = target?.dataset.collageCellFilled === 'true'
        && Number.isInteger(targetIndex)
        && targetIndex !== current.cellIndex
        ? targetIndex
        : null
      dropTarget.current = nextTarget
      setDropTargetIndex(nextTarget)
    },
    onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
      const current = active.current
      if (!current || current.id !== event.pointerId) return
      clearLongPress()
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      if (dragging.current) {
        if (dropTarget.current !== null) swapSelectedCollageCells(current.cellIndex, dropTarget.current)
      } else if (!current.cancelledTap) {
        activateCell(current.cellIndex)
      }
      reset()
    },
    onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => {
      if (active.current?.id === event.pointerId) reset()
    },
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (event.detail === 0) activateCell(eventCell(event.currentTarget).index)
    },
  }

  useEffect(() => () => clearLongPress(), [])

  return { dragSourceIndex, dropTargetIndex, handlers }
}
